// Draws a self-contained link-preview card onto a canvas and exports it as PNG.
//
// The card carries its own opaque light surface so text stays legible on any
// recipient theme; transparency is used only for the rounded-corner cut-outs and
// the soft drop shadow, which composite cleanly onto any chat background.
//
// PNG is used for universal decodeability. Since PNG has no quality knob, size is
// controlled by pixel dimensions: we render at 2x for crisp text and step the
// scale down until the encoded image fits the byte budget.

import type { LinkMetadata, PreviewLayout } from './openGraph'

const BUDGET_BYTES = 600 * 1024 // keep previews well under ~0.6 MB
const SCALES = [2, 1.5, 1.25, 1]

// Locked light-surface palette (see docs/link-preview.md).
const COLORS = {
  surface: '#f4f6f9',
  title: '#121821',
  desc: '#525d68',
  host: '#0e7f5b',
  border: 'rgba(16,26,38,.14)',
  shadow: 'rgba(8,14,22,.30)',
}

const CARD_W = 452 // logical card width (css px), excluding the shadow margin
const RADIUS = 14
const INNER_PAD = 16
const HERO_H = 194
const THUMB = 78 // compact side-thumbnail size

export type Drawable = {
  image: CanvasImageSource
  naturalWidth: number
  naturalHeight: number
} | null

export type RenderInput = {
  meta: LinkMetadata
  thumbnail: Drawable
  layout: PreviewLayout
}

export type RenderedCard = {
  /** base64 PNG (no data: prefix), ready for writeTempFileFromBase64. */
  base64: string
  width: number
  height: number
  bytes: number
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  const rr = Math.min(r, w / 2, h / 2)
  ctx.beginPath()
  ctx.moveTo(x + rr, y)
  ctx.arcTo(x + w, y, x + w, y + h, rr)
  ctx.arcTo(x + w, y + h, x, y + h, rr)
  ctx.arcTo(x, y + h, x, y, rr)
  ctx.arcTo(x, y, x + w, y, rr)
  ctx.closePath()
}

/** Wrap `text` to `maxW`, clamp to `maxLines`, ellipsis the last line. */
function wrapLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxW: number,
  maxLines: number,
  font: string
): string[] {
  ctx.font = font
  const words = text.split(/\s+/).filter(Boolean)
  const lines: string[] = []
  let line = ''
  for (const w of words) {
    const test = line ? line + ' ' + w : w
    if (ctx.measureText(test).width > maxW && line) {
      lines.push(line)
      line = w
      if (lines.length === maxLines) {
        break
      }
    } else {
      line = test
    }
  }
  if (lines.length < maxLines && line) {
    lines.push(line)
  }
  const consumedWords = lines.join(' ').split(/\s+/).filter(Boolean).length
  if (consumedWords < words.length) {
    let last = lines[lines.length - 1] || ''
    while (last.length && ctx.measureText(last + '…').width > maxW) {
      last = last.slice(0, -1)
    }
    lines[lines.length - 1] = last.replace(/\s+$/, '') + '…'
  }
  return lines
}

/** Draw pre-wrapped lines; returns the y of the last baseline. */
function drawLines(
  ctx: CanvasRenderingContext2D,
  lines: string[],
  x: number,
  y: number,
  lineH: number,
  font: string,
  color: string
): number {
  ctx.font = font
  ctx.fillStyle = color
  ctx.textBaseline = 'alphabetic'
  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i] ?? '', x, y + lineH * (i + 1))
  }
  return y + lineH * lines.length
}

function drawHost(
  ctx: CanvasRenderingContext2D,
  host: string,
  x: number,
  y: number
) {
  const s = 12
  roundRect(ctx, x, y - s + 1, s, s, 3)
  ctx.fillStyle = COLORS.host
  ctx.fill()
  ctx.font = '650 12.5px system-ui, sans-serif'
  ctx.fillStyle = COLORS.host
  ctx.fillText(host.toUpperCase(), x + s + 7, y)
}

/** Draw a "cover"-fitted image into a clipped rounded rect. */
function drawCover(
  ctx: CanvasRenderingContext2D,
  d: Drawable,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  if (!d) {
    // Neutral placeholder block so layout is stable even without an image.
    roundRect(ctx, x, y, w, h, r)
    ctx.fillStyle = '#dfe4ea'
    ctx.fill()
    return
  }
  ctx.save()
  roundRect(ctx, x, y, w, h, r)
  ctx.clip()
  const scale = Math.max(w / d.naturalWidth, h / d.naturalHeight)
  const dw = d.naturalWidth * scale
  const dh = d.naturalHeight * scale
  ctx.drawImage(d.image, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh)
  ctx.restore()
}

function drawPlayGlyph(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number
) {
  const r = 26
  ctx.save()
  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.fillStyle = 'rgba(8,12,18,.55)'
  ctx.fill()
  ctx.beginPath()
  ctx.moveTo(cx - 8, cy - 12)
  ctx.lineTo(cx - 8, cy + 12)
  ctx.lineTo(cx + 13, cy)
  ctx.closePath()
  ctx.fillStyle = '#ffffff'
  ctx.fill()
  ctx.restore()
}

/** Render one canvas at `scale` (device pixels per css px). */
function buildCanvas(input: RenderInput, scale: number): HTMLCanvasElement {
  const { meta, thumbnail, layout } = input
  const PAD = 28
  const hasThumb = thumbnail != null || layout === 'hero'
  const titleFont = '600 17px system-ui, sans-serif'
  const descFont = '400 14px system-ui, sans-serif'

  // Measure the wrapped text first so the card is sized to its actual content
  // (a fixed worst-case height leaves a blank band under short titles).
  const c = document.createElement('canvas')
  const ctx = c.getContext('2d')!
  const sideThumb = hasThumb && layout !== 'hero'
  const textW = CARD_W - INNER_PAD * 2 - (sideThumb ? THUMB + 14 : 0)
  const titleLines = wrapLines(ctx, meta.title, textW, 2, titleFont)
  const descLines = meta.description
    ? wrapLines(ctx, meta.description, textW, sideThumb ? 1 : 2, descFont)
    : []
  // title block + optional description block + host row (baseline offset 20,
  // ~6px descender allowance)
  const textH =
    22 * titleLines.length +
    (descLines.length ? 4 + 20 * descLines.length : 0) +
    20 + 6

  let cardH: number
  if (layout === 'hero') {
    cardH = HERO_H + 4 + textH + INNER_PAD
  } else {
    const textCardH = INNER_PAD - 2 + textH + INNER_PAD
    cardH = sideThumb ? Math.max(THUMB + INNER_PAD * 2, textCardH) : textCardH
  }
  cardH = Math.round(cardH)

  const wCss = CARD_W + PAD * 2
  const hCss = cardH + PAD * 2

  c.width = Math.round(wCss * scale)
  c.height = Math.round(hCss * scale)
  ctx.scale(scale, scale)

  // surface + shadow
  ctx.save()
  ctx.shadowColor = COLORS.shadow
  ctx.shadowBlur = 22
  ctx.shadowOffsetY = 8
  ctx.fillStyle = COLORS.surface
  roundRect(ctx, PAD, PAD, CARD_W, cardH, RADIUS)
  ctx.fill()
  ctx.restore()

  // hairline border
  ctx.lineWidth = 1
  ctx.strokeStyle = COLORS.border
  roundRect(ctx, PAD + 0.5, PAD + 0.5, CARD_W - 1, cardH - 1, RADIUS)
  ctx.stroke()

  // clip content to the card
  ctx.save()
  roundRect(ctx, PAD, PAD, CARD_W, cardH, RADIUS)
  ctx.clip()

  const ox = PAD
  const oy = PAD
  const tx = ox + INNER_PAD

  if (layout === 'hero') {
    drawCover(ctx, thumbnail, ox, oy, CARD_W, HERO_H, 0)
    if (meta.isVideo) {
      drawPlayGlyph(ctx, ox + CARD_W / 2, oy + HERO_H / 2)
    }
    const afterT = drawLines(
      ctx, titleLines, tx, oy + HERO_H + 4, 22, titleFont, COLORS.title
    )
    const afterD = descLines.length
      ? drawLines(ctx, descLines, tx, afterT + 4, 20, descFont, COLORS.desc)
      : afterT
    drawHost(ctx, meta.host, tx, afterD + 20)
  } else {
    if (sideThumb) {
      const thx = ox + CARD_W - INNER_PAD - THUMB
      const thy = oy + (cardH - THUMB) / 2
      drawCover(ctx, thumbnail, thx, thy, THUMB, THUMB, Math.max(8, RADIUS - 6))
    }
    const afterT = drawLines(
      ctx, titleLines, tx, oy + INNER_PAD - 2, 22, titleFont, COLORS.title
    )
    const afterD = descLines.length
      ? drawLines(ctx, descLines, tx, afterT + 4, 20, descFont, COLORS.desc)
      : afterT
    drawHost(ctx, meta.host, tx, afterD + 20)
  }
  ctx.restore()
  return c
}

function toPngBase64(
  canvas: HTMLCanvasElement
): Promise<{ base64: string; bytes: number }> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (!blob) {
        reject(new Error('canvas.toBlob returned null'))
        return
      }
      const reader = new FileReader()
      reader.onerror = () => reject(reader.error)
      reader.onloadend = () => {
        const dataUrl = String(reader.result)
        const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1)
        resolve({ base64, bytes: blob.size })
      }
      reader.readAsDataURL(blob)
    }, 'image/png')
  })
}

/**
 * Render the card and return a PNG that fits the byte budget, stepping the
 * pixel scale down until it does (last resort: return the smallest attempt).
 */
export async function renderCardPng(input: RenderInput): Promise<RenderedCard> {
  let smallest: RenderedCard | null = null
  for (const scale of SCALES) {
    const canvas = buildCanvas(input, scale)
    const { base64, bytes } = await toPngBase64(canvas)
    const result = {
      base64,
      bytes,
      width: canvas.width,
      height: canvas.height,
    }
    if (bytes <= BUDGET_BYTES) {
      return result
    }
    if (!smallest || bytes < smallest.bytes) {
      smallest = result
    }
  }
  // Everything was over budget (huge/complex thumbnail); return the smallest.
  return smallest as RenderedCard
}
